import React, { useMemo, useState } from 'react';
import {
  FaPlay,
  FaDownload,
  FaPlus,
  FaTrash,
  FaArrowRotateLeft,
  FaTriangleExclamation,
} from 'react-icons/fa6';
import Badge from '@/components/Badges';
import { runTransformTest } from '@/api';
import { apiErrorMessage, validateStepDraft } from './constants';
import {
  DEFAULT_TEST_LIMIT,
  FORMAT_OPTIONS,
  buildStepsPayload,
  draftFromSteps,
  parseTransformCsv,
  downloadCsv,
  validateS3Key,
  validateLimit,
  buildFunnel,
} from './transformTest';

const PREVIEW_ROWS = 100;

const RowFunnel = ({ stages }) => (
  <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
    {stages.map((stage, i) => (
      <div key={i} className="flex items-center gap-2 shrink-0">
        {i > 0 && (
          <span
            className={`text-xs ${
              stage.dropped > 0
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-slate-300 dark:text-slate-600'
            }`}
            title={stage.dropped > 0 ? `${stage.dropped.toLocaleString()} rows dropped` : 'no rows dropped'}
          >
            →{stage.dropped > 0 ? ` −${stage.dropped.toLocaleString()}` : ''}
          </span>
        )}
        <div className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent text-center">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {typeof stage.rows === 'number' ? stage.rows.toLocaleString() : '—'}
          </div>
          <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 truncate max-w-[10rem]">
            {stage.label}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Run a store's chain over a real feed file and show what comes out.
 *
 * Two modes, and the second is the reason the endpoint exists: the saved chain
 * (disabled steps included, so you can see what a step would do before enabling
 * it) or an edited chain typed here and never saved.
 */
const TransformTestPanel = ({ storeId, steps, multiFile = false, trigger = null }) => {
  const [s3Key, setS3Key] = useState('');
  const [format, setFormat] = useState('final');
  const [limit, setLimit] = useState(String(DEFAULT_TEST_LIMIT));
  const [useDraft, setUseDraft] = useState(false);
  const [draft, setDraft] = useState(() => draftFromSteps(steps));

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const keyError = s3Key ? validateS3Key(s3Key, storeId) : null;

  // For a multi-file store the pod derives the sibling files from the
  // *triggering* upload's name, so a non-trigger key is not a supported input.
  // Advisory rather than blocking: the contract predates multi-source and what
  // the pod does with a non-trigger key is unconfirmed.
  const triggerHint = (() => {
    if (!multiFile || !s3Key || keyError) return null;
    if (!trigger) {
      return 'This store has no trigger input, so there is no key the pipeline would treat as the start of a cycle. Fix the manifest above first.';
    }
    const key = s3Key.trim().toLowerCase();
    const expected = (trigger.pattern || trigger.fileName || '').toLowerCase();
    if (trigger.pattern) {
      try {
        if (new RegExp(`^(?:${trigger.pattern})$`, 'i').test(key.split('/').pop())) return null;
      } catch {
        return null;
      }
    } else if (expected && key.endsWith(expected)) {
      return null;
    }
    return `This does not look like the trigger file (${trigger.pattern ? `pattern ${trigger.pattern}` : trigger.fileName}). The pod derives the sibling files from the triggering upload's name, so send the trigger's key.`;
  })();
  const limitError = validateLimit(limit);

  const draftError = useMemo(() => {
    if (!useDraft) return null;
    if (draft.length === 0) return 'Add at least one step, or switch back to the saved chain.';
    // Index into the original array, not the filtered one, so a duplicate-name
    // message reports the position the user actually sees.
    const positioned = draft.map((d, j) => ({ id: `draft-${j}`, name: d.name, stepOrder: j + 1 }));
    for (let i = 0; i < draft.length; i += 1) {
      const message = validateStepDraft(
        draft[i],
        positioned.filter((_, j) => j !== i),
        null
      );
      if (message) return `Step ${i + 1}: ${message}`;
    }
    return null;
  }, [useDraft, draft]);

  const blocked = !s3Key || !!keyError || !!limitError || !!draftError;

  const run = async ({ download = false } = {}) => {
    setRunning(true);
    setError(null);
    try {
      const payload = {
        storeId,
        s3Key: s3Key.trim(),
        format,
        limit: download ? 0 : Number(limit) || 0,
      };
      if (useDraft) payload.steps = buildStepsPayload(draft);

      const res = await runTransformTest(payload);

      if (download) {
        downloadCsv(res.csv, res.filename || `${storeId}_transform_test.csv`);
        setRunning(false);
        return;
      }

      setResult({ ...res, parsed: parseTransformCsv(res.csv) });
    } catch (err) {
      const status = err?.response?.status;
      // 422 is the normal answer for a broken chain, not a system fault: the
      // message names the step and the SQL error, so show it verbatim.
      setError({
        status,
        message: apiErrorMessage(err, 'Transform test failed'),
        retryable: status === 503,
      });
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const chainForFunnel = useDraft ? draft.map((d) => ({ name: d.name })) : steps;
  const funnel = result ? buildFunnel(result.sourceRows, result.stepRows, chainForFunnel) : null;

  return (
    <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
          Test transform
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Runs the chain over a real feed file and returns the result. Nothing is saved and the
          pipeline is not touched.
        </p>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            S3 key
          </label>
          <input
            type="text"
            value={s3Key}
            onChange={(e) => setS3Key(e.target.value)}
            placeholder={`${storeId}/inv_20260828_0900.csv`}
            spellCheck={false}
            className="w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Relative to the <span className="font-mono">saucey-menu-ftp</span> bucket — no bucket
            name, no leading slash. Chain stores use{' '}
            <span className="font-mono">{'{chain}/{store}/{file}'}</span>.
          </p>
          {keyError && (
            <p className="mt-1 text-xs text-rose-500 dark:text-rose-400">{keyError}</p>
          )}
          {multiFile && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              This is a multi-file feed
              {trigger ? (
                <>
                  {' '}
                  — send the trigger&rsquo;s key, ending in{' '}
                  <span className="font-mono">{trigger.fileName}</span>. The pipeline finds the
                  sibling files from it.
                </>
              ) : (
                '.'
              )}
            </p>
          )}
          {triggerHint && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{triggerHint}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Output format
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {FORMAT_OPTIONS.find((o) => o.value === format)?.help}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Row limit
            </label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min="0"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Keep this modest for the interactive view; Download CSV always fetches every row.
            </p>
            {limitError && (
              <p className="mt-1 text-xs text-rose-500 dark:text-rose-400">{limitError}</p>
            )}
          </div>
        </div>

        <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useDraft}
                onChange={(e) => {
                  setUseDraft(e.target.checked);
                  if (e.target.checked) setDraft(draftFromSteps(steps));
                }}
                className="mt-0.5 form-checkbox"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                Run an edited chain
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Runs ALL steps, including disabled ones. Preferred way to test a chain before saving.
                </span>
              </span>
            </label>
            {useDraft && (
              <button
                onClick={() => setDraft(draftFromSteps(steps))}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <FaArrowRotateLeft className="w-3 h-3" />
                Reset from saved
              </button>
            )}
          </div>

          {useDraft && (
            <div className="mt-3 space-y-3">
              {draft.map((step, index) => (
                <div
                  key={step.key}
                  className="rounded border border-slate-200 dark:border-slate-700 p-3"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500">
                      {index + 1}
                    </span>
                    <input
                      type="text"
                      value={step.name}
                      onChange={(e) =>
                        setDraft(
                          draft.map((d, i) => (i === index ? { ...d, name: e.target.value } : d))
                        )
                      }
                      placeholder="step_name"
                      spellCheck={false}
                      className="flex-1 px-2 py-1 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    />
                    {index === draft.length - 1 && (
                      <Badge color="purple" size="xs">
                        Output
                      </Badge>
                    )}
                    <button
                      onClick={() => setDraft(draft.filter((_, i) => i !== index))}
                      title="Remove this step"
                      className="p-1 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <textarea
                    value={step.sql}
                    onChange={(e) =>
                      setDraft(draft.map((d, i) => (i === index ? { ...d, sql: e.target.value } : d)))
                    }
                    rows={5}
                    spellCheck={false}
                    placeholder={
                      index === 0
                        ? 'SELECT ItemNum AS retailer_sku, Price AS retailer_price FROM source'
                        : `SELECT * FROM ${draft[index - 1]?.name || 'previous_step'}`
                    }
                    className="mt-2 w-full px-2 py-1.5 font-mono text-xs border border-slate-300 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-900/40 text-slate-900 dark:text-slate-100"
                  />
                </div>
              ))}

              <button
                onClick={() =>
                  setDraft([...draft, { key: `new-${Date.now()}`, name: '', sql: '' }])
                }
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                <FaPlus className="w-3.5 h-3.5" />
                Add step
              </button>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                The raw CSV is table <span className="font-mono">source</span>. The{' '}
                <strong>last step</strong> is read for output and must emit{' '}
                <span className="font-mono">retailer_sku</span> and{' '}
                <span className="font-mono">retailer_price</span>. In SQL string literals a
                backslash needs no escaping here — the API client handles JSON encoding.
              </p>

              {draftError && (
                <p className="text-xs text-rose-500 dark:text-rose-400">{draftError}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => run()}
            disabled={running || blocked}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlay className="w-3.5 h-3.5 mr-2" />
            {running ? 'Running…' : 'Run test'}
          </button>
          <button
            onClick={() => run({ download: true })}
            disabled={running || blocked}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaDownload className="w-3.5 h-3.5 mr-2" />
            Download all rows
          </button>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            One test runs at a time, 60s timeout.
          </span>
        </div>

        {error && (
          <div className="rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
            <div className="flex items-start gap-2">
              <FaTriangleExclamation className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-rose-800 dark:text-rose-200">
                  {error.status === 422
                    ? 'The chain failed to run'
                    : error.status === 503
                      ? 'Another test is already running'
                      : 'Transform test failed'}
                </p>
                <p className="mt-1 text-sm text-rose-700 dark:text-rose-300 font-mono break-words">
                  {error.message}
                </p>
                {error.retryable && (
                  <button
                    onClick={() => run()}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-rose-600 rounded hover:bg-rose-700"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {funnel ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
                  Rows per stage
                </p>
                <RowFunnel stages={funnel} />
              </div>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Per-stage row counts unavailable — the{' '}
                <span className="font-mono">X-CDC-Step-Rows</span> header did not reach the browser.
              </p>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {result.parsed.rows.length.toLocaleString()} row
                {result.parsed.rows.length === 1 ? '' : 's'} returned
                {result.parsed.rows.length > PREVIEW_ROWS &&
                  ` — showing the first ${PREVIEW_ROWS}`}
              </p>
              {result.csv && (
                <button
                  onClick={() =>
                    downloadCsv(result.csv, result.filename || `${storeId}_transform_test.csv`)
                  }
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <FaDownload className="w-3 h-3" />
                  Download these rows
                </button>
              )}
            </div>

            {result.parsed.rows.length === 0 ? (
              <div className="rounded border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  The chain ran and produced no rows.
                </p>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  Check the stage counts above for where they went — a date filter against an old
                  file is the usual cause.
                </p>
              </div>
            ) : (
              <div className="border border-slate-200 dark:border-slate-700 rounded overflow-auto max-h-96">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-900/40 sticky top-0">
                    <tr>
                      {result.parsed.headers.map((header, i) => (
                        <th
                          key={i}
                          className="px-3 py-2 text-left font-semibold font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap border-b border-slate-200 dark:border-slate-700"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.parsed.rows.slice(0, PREVIEW_ROWS).map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 dark:border-slate-800 last:border-0"
                      >
                        {result.parsed.headers.map((_, j) => (
                          <td
                            key={j}
                            className="px-3 py-1.5 font-mono text-slate-700 dark:text-slate-200 whitespace-nowrap"
                          >
                            {row[j] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransformTestPanel;
